import { EventStatus, EventType, type MatrixEvent, MsgType, RelationType, type Room } from 'matrix-js-sdk'
import type { IMessage, IMessageAttachment } from '@/modules/chat-matrix-fast/types/IChat'
import {
  DELETED_MESSAGE_TEXT,
  MATRIX_FORWARD_CONTENT_KEY,
  MATRIX_PIN_NOTICE_CONTENT_KEY,
} from '@/modules/chat-matrix-fast/constants/chat'

const ENCRYPTED_MESSAGE_PLACEHOLDER = 'Зашифрованное сообщение'

function getMessageTransactionId(event: MatrixEvent): string | undefined {
  return event.getTxnId?.() || event.getUnsigned().transaction_id
}

export type MatrixEventSendStatus = 'sending' | 'not_sent' | 'sent'

export function resolveMatrixEventSendStatus(event: MatrixEvent): MatrixEventSendStatus | undefined {
  const eventId = event.getId()
  const status = event.status

  if (eventId && !eventId.startsWith('~')) {
    if (status === EventStatus.NOT_SENT) {
      return 'not_sent'
    }
    if (status === EventStatus.SENT || status === null) {
      return 'sent'
    }
    return undefined
  }

  switch (status) {
    case EventStatus.SENDING:
    case EventStatus.ENCRYPTING:
    case EventStatus.CANCELLED:
      return 'sending'
    case EventStatus.NOT_SENT:
      return 'not_sent'
    case EventStatus.SENT:
      return 'sent'
    default:
      return getMessageTransactionId(event) ? 'sending' : undefined
  }
}

export function hashToNumber(value: string): number {
  let hash = 0x811C9DC5
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }

  return hash >>> 0
}

export function looksLikeMatrixEventId(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('$') && value.length > 1
}

function extractInReplyToEventId(content: Record<string, unknown>): string | undefined {
  const relatesTo = content['m.relates_to']
  if (!relatesTo || typeof relatesTo !== 'object') {
    return undefined
  }

  const inReplyTo = (relatesTo as Record<string, unknown>)['m.in_reply_to']
  if (!inReplyTo || typeof inReplyTo !== 'object') {
    return undefined
  }

  const eventId = (inReplyTo as Record<string, unknown>).event_id
  return looksLikeMatrixEventId(eventId) ? eventId : undefined
}

export function isReplaceEvent(event: MatrixEvent): boolean {
  return event.getRelation()?.rel_type === RelationType.Replace
}

export function resolveMatrixEventCollectionKey(event: MatrixEvent): string | null {
  const eventId = event.getId()
  if (eventId) {
    return eventId
  }

  const txnId = event.getTxnId?.()
  if (txnId) {
    return `$txn:${txnId}`
  }

  const senderId = event.getSender() ?? 'unknown'
  const ts = event.getTs()
  const type = event.getType()

  if (!type || !ts) {
    return null
  }

  return `$local:${type}:${senderId}:${ts}`
}

function stripFormattedBody(value: unknown): string {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim()
}

function extractMentionUserIds(content: Record<string, unknown> | null | undefined): string[] | undefined {
  if (!content) {
    return undefined
  }

  const fromMentions = (value: unknown): string[] => {
    if (!value || typeof value !== 'object') {
      return []
    }
    const userIds = (value as { user_ids?: unknown }).user_ids
    if (!Array.isArray(userIds)) {
      return []
    }
    return userIds.filter((id): id is string => typeof id === 'string' && id.startsWith('@'))
  }

  const root = fromMentions(content['m.mentions'])
  const nested = content['m.new_content']
  const fromNew = nested && typeof nested === 'object'
    ? fromMentions((nested as Record<string, unknown>)['m.mentions'])
    : []

  const ids = [...new Set([...fromNew, ...root])]
  return ids.length ? ids : undefined
}

function getServerAggregatedReplacementContent(event: MatrixEvent): Record<string, unknown> | null {
  if (event.replacingEvent?.()) {
    return null
  }

  const aggregated = event.getServerAggregatedRelation?.<Record<string, unknown>>(RelationType.Replace)
  if (!aggregated || typeof aggregated !== 'object') {
    return null
  }

  if (typeof aggregated.sender === 'string' && aggregated.sender !== event.getSender()) {
    return null
  }

  const content = aggregated.content as Record<string, unknown> | undefined
  const newContent = content?.['m.new_content']
  return newContent && typeof newContent === 'object'
    ? newContent as Record<string, unknown>
    : null
}

export function resolveMatrixMessageContent(event: MatrixEvent): Record<string, unknown> | null {
  if (event.getType() === EventType.RoomMessage || event.getType() === EventType.Sticker) {
    return getServerAggregatedReplacementContent(event) ?? event.getContent()
  }

  if (event.getType() === EventType.RoomMessageEncrypted) {
    const clearContent = event.getClearContent?.()
    if (clearContent && typeof clearContent === 'object') {
      return clearContent as Record<string, unknown>
    }
  }

  return null
}

function createEncryptedPlaceholderMessage(event: MatrixEvent): IMessage {
  const senderId = event.getSender() ?? ''
  const sender = event.sender
  const eventId = event.getId()
  const mappedId = hashToNumber(eventId ?? `${senderId}:${event.getTs()}`)

  return {
    id: mappedId,
    matrix_event_id: eventId ?? undefined,
    matrix_txn_id: getMessageTransactionId(event),
    content: ENCRYPTED_MESSAGE_PLACEHOLDER,
    author: senderId,
    author_id: senderId,
    author_name: sender?.name ?? senderId,
    created_by: {
      id: senderId,
      full_name: sender?.name ?? senderId,
      user_name: senderId,
      username: senderId,
    },
    attachments: [],
    created_at: new Date(event.getTs()).toISOString(),
    is_read: false,
    is_delivered: false,
  }
}

function isAttachmentMessage(content: Record<string, unknown>): boolean {
  const msgtype = String(content.msgtype || '')

  if (!content.url) {
    return false
  }

  return msgtype === MsgType.Image
    || msgtype === MsgType.Video
    || msgtype === 'm.image'
    || msgtype === 'm.video'
    || msgtype === MsgType.File
    || msgtype === 'm.file'
}

function parseMessageAttachment(
  content: Record<string, unknown>,
  mappedId: number,
): IMessageAttachment | null {
  const mxcUrl = typeof content.url === 'string' ? content.url : ''
  if (!mxcUrl) {
    return null
  }

  const info = content.info as Record<string, unknown> | undefined
  const filename = typeof content.filename === 'string'
    ? content.filename
    : typeof content.body === 'string'
      && /\.[a-z0-9]{2,5}$/i.test(content.body.trim())
      && !content.body.includes('\n')
      ? content.body.trim()
      : undefined
  const thumbnail = typeof info?.thumbnail_url === 'string'
    ? info.thumbnail_url
    : undefined

  return {
    id: mappedId,
    file: mxcUrl,
    mime_type: typeof info?.mimetype === 'string' ? info.mimetype : undefined,
    name: filename,
    thumbnail,
  }
}

function createDeletedMessage(event: MatrixEvent): IMessage {
  const senderId = event.getSender() ?? ''
  const sender = event.sender
  const eventId = event.getId()
  const mappedId = hashToNumber(eventId ?? `${senderId}:${event.getTs()}`)

  return {
    id: mappedId,
    matrix_event_id: eventId ?? undefined,
    matrix_txn_id: getMessageTransactionId(event),
    content: DELETED_MESSAGE_TEXT,
    author: senderId,
    author_id: senderId,
    author_name: sender?.name ?? senderId,
    created_by: {
      id: senderId,
      full_name: sender?.name ?? senderId,
      user_name: senderId,
      username: senderId,
    },
    attachments: [],
    created_at: new Date(event.getTs()).toISOString(),
    is_read: false,
    is_delivered: false,
    is_deleted: true,
  }
}

export function markMessageAsDeleted(message: IMessage): IMessage {
  return {
    ...message,
    content: DELETED_MESSAGE_TEXT,
    attachments: [],
    is_deleted: true,
  }
}

export function eventToMessage(
  event: MatrixEvent,
): IMessage | null {
  const eventType = event.getType()
  if (
    eventType !== EventType.RoomMessage
    && eventType !== EventType.RoomMessageEncrypted
    && eventType !== EventType.Sticker
  ) {
    return null
  }

  if (isReplaceEvent(event)) {
    return null
  }

  if (event.isRedacted()) {
    return createDeletedMessage(event)
  }

  const content = resolveMatrixMessageContent(event)
  if (!content) {
    return eventType === EventType.RoomMessageEncrypted
      ? createEncryptedPlaceholderMessage(event)
      : null
  }

  const isAttachment = isAttachmentMessage(content)
  const messageBodyCandidate = String(content.body || '').trim()
    || stripFormattedBody(content.formatted_body)

  if (
    !messageBodyCandidate
    && !(content?.['m.new_content'] as { body?: unknown } | undefined)?.body
    && !isAttachment
    && !content?.url
  ) {
    return null
  }

  const sender = event.sender
  const senderId = event.getSender() ?? ''
  const eventId = event.getId()
  const txnId = getMessageTransactionId(event)
  // Local echo без getId(): pending id, чтобы edit/delete не теряли событие
  const matrixEventId = eventId || (txnId ? `~${txnId}` : undefined)
  const mappedId = hashToNumber(matrixEventId ?? `${senderId}:${event.getTs()}`)

  let messageBody = messageBodyCandidate
  const isEdited = Boolean(event.replacingEventId?.() || event.replacingEvent?.())

  const attachments: IMessageAttachment[] = []
  if (isAttachment) {
    const attachment = parseMessageAttachment(content, mappedId)
    if (attachment) {
      attachments.push(attachment)
    }
  }

  // body без подписи = имя файла — не дублируем его текстом сообщения
  if (attachments.length > 0) {
    const filename = typeof content.filename === 'string'
      ? content.filename
      : attachments[0]?.name || ''

    if (filename && messageBody === filename) {
      messageBody = ''
    }
  }

  const replyEventId = extractInReplyToEventId(content)
    ?? extractInReplyToEventId(event.getOriginalContent() as Record<string, unknown>)
  const matrixEventStatus = resolveMatrixEventSendStatus(event)

  return {
    id: mappedId,
    matrix_event_id: matrixEventId,
    matrix_txn_id: txnId,
    matrix_event_status: matrixEventStatus,
    content: messageBody,
    author: senderId,
    author_id: senderId,
    author_name: sender?.name ?? senderId,
    created_by: {
      id: senderId,
      full_name: sender?.name ?? senderId,
      user_name: senderId,
      username: senderId,
    },
    attachments,
    created_at: new Date(event.getTs()).toISOString(),
    is_read: false,
    is_delivered: false,
    is_edited: isEdited,
    is_system: Boolean(
      (event.getContent() as Record<string, unknown>)?.[MATRIX_PIN_NOTICE_CONTENT_KEY],
    ),
    system_target_event_id: (() => {
      const meta = (event.getContent() as Record<string, unknown>)?.[MATRIX_PIN_NOTICE_CONTENT_KEY] as
        | { event_id?: string }
        | undefined
      return typeof meta?.event_id === 'string' ? meta.event_id : undefined
    })(),
    mention_user_ids: extractMentionUserIds(content),
    forward: parseForwardMeta(content),
    ...(replyEventId
      ? {
          reply_to_id: replyEventId,
          reply_message_id: replyEventId,
        }
      : {}),
  }
}

function parseForwardMeta(content: Record<string, unknown>): IMessage['forward'] | undefined {
  const raw = content[MATRIX_FORWARD_CONTENT_KEY]
  if (!raw || typeof raw !== 'object') {
    return undefined
  }

  const meta = raw as Record<string, unknown>
  const originalSender = typeof meta.original_sender_display === 'string'
    ? meta.original_sender_display.trim()
    : ''

  return {
    forward_id: typeof meta.forward_id === 'string' ? meta.forward_id : undefined,
    original_sender_display: originalSender || undefined,
    source_room_id: typeof meta.source_room_id === 'string' ? meta.source_room_id : undefined,
    source_event_id: typeof meta.source_event_id === 'string' ? meta.source_event_id : undefined,
  }
}

export function collectRoomMatrixEvents(room: Room): MatrixEvent[] {
  const byKey = new Map<string, MatrixEvent>()

  const addEvent = (event: MatrixEvent) => {
    const key = resolveMatrixEventCollectionKey(event)
    if (!key || byKey.has(key)) {
      return
    }
    byKey.set(key, event)
  }

  for (const event of room.getLiveTimeline().getEvents()) {
    addEvent(event)
  }

  if (room.client?.supportsThreads()) {
    for (const thread of room.getThreads()) {
      for (const event of thread.events) {
        addEvent(event)
      }
    }
  }

  return [...byKey.values()].sort((a, b) => a.getTs() - b.getTs())
}

export function attachReplySourcesToMessages(messages: IMessage[]): IMessage[] {
  const byEventId = new Map<string, IMessage>()
  for (const message of messages) {
    if (message.matrix_event_id && !message.matrix_event_id.startsWith('~')) {
      byEventId.set(message.matrix_event_id, message)
    }
  }

  return messages.map(message => {
    if (message.reply_to) {
      return message
    }

    const replyId = message.reply_message_id || message.reply_to_id
    if (!replyId || typeof replyId !== 'string') {
      return message
    }

    const source = byEventId.get(replyId)

    if (!source) {
      return message
    }

    return {
      ...message,
      reply_to: source,
    }
  })
}

export function mapMatrixEventsToMessages(events: MatrixEvent[]): IMessage[] {
  const mapped = events
    .map(event => eventToMessage(event))
    .filter((message): message is IMessage => Boolean(message))
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))

  return attachReplySourcesToMessages(mapped)
}

export function getMatrixMessagesFromRoom(room: Room): IMessage[] {
  return mapMatrixEventsToMessages(collectRoomMatrixEvents(room))
}
